// Full-document render of the tempurpedic BasesListPage: PageSkeleton
// wrapping the page body in order (normal site chrome; bases-list page CSS
// via page_chrome):
//
// 1. The hero (rendered only when the page has a hero_image): width-1600
//    rendition inline background and the conditionally-rendered desktop copy,
//    plus the ALWAYS-rendered mobile copy block. Unlike ProductListPage's
//    hero, the mobile title branches (legacy quirk preserved): a set
//    hero_title_mobile renders in a bare `rich-text` div, the desktop
//    fallback keeps the full class list, neither renders no div. The legacy
//    badge block referenced a `hero_badge` field the model doesn't have and
//    never rendered -- not carried.
// 2. `.product-lists`: the bed-bases grid (h1 title, `rich-text`-classed
//    subtitle) and the foundations grid (h2, plain subtitle) of shared
//    product cards; the foundation cards carry the FoundationListBlock
//    title/copy overrides (server-applied).
// 3. The static `.bed-bases-selection` 3-column section (server-gettext'd
//    copy). `.bed-bases-selection__content` hosts the mobile Flickity carousel
//    (below).
// 4. The `.bed-bases-callout` over `extra_copy` -- a bound-block loop
//    (NO w-block wrappers, Fragment mode) with the bespoke heading /
//    link_banner branches, the render_basic primitives, the value_prop_bar
//    alias, and the Toolbox keys through the page map.
//
// The legacy template's multi-line {% wagtail_image %} / {% static_image %} /
// {% trans %} tags never lexed as tags (Django tags cannot span source
// lines) and printed themselves as literal text -- the grid header images,
// selection images/copy, and callout banner image here render the markup
// those tags declared.
import { Immutable } from "immer";
import React from "react";
import { RawHTMLBlock } from "tsi-common-react/src/components/RawHTMLBlock/index";
import { ValuePropsBarBlock } from "tsi-common-react/src/components/ValuePropsBarBlock/index";
import { useFlickity } from "tsi-common-react/src/hooks/useFlickity";
import { useStaticURL } from "tsi-common-react/src/utils/urls";

import type { LinkImageBlock as LinkImageBlockValue } from "@reactivated";
import { templates } from "@reactivated";
import type {
    CharBlock as CharBlockValue,
    ImageChooserBlock as ImageChooserBlockValue,
    RawHTMLBlock as RawHTMLBlockValue,
    RichTextBlock as RichTextBlockValue,
    ValuePropBarBlock as ValuePropBarBlockValue,
} from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { buildToolboxPageMap } from "../blocks/toolboxPageMap";
import { PageSkeleton } from "../components/PageSkeleton";
import { ProductCard } from "../components/ProductCard";

// Mutable reactivated props (not Immutable<>): this template composes the
// in-tree SiteHeader chain via PageSkeleton, which is typed with mutable
// reactivated props (see PageSkeleton).
type IProps = templates.BasesListPageTemplate;
type ILinkBannerValue = LinkImageBlockValue;

const Hero = (props: {
    hero: NonNullable<IProps["hero"]>;
}): React.JSX.Element => {
    const { hero } = props;
    return (
        <section
            className="hero product-list-hero product-list-hero--shop-pillows-hero l-full-width"
            style={
                hero.background_image_url
                    ? {
                          backgroundImage: `url(${hero.background_image_url})`,
                      }
                    : undefined
            }
        >
            <div
                className="product-list-hero__overlay product-list-hero__overlay--shop-pillows-hero"
                aria-hidden="true"
            ></div>
            <div className="l-capped-width product-list-hero__inner">
                <div className="product-list-hero__content product-list-hero__content--pillows">
                    {hero.pre_title && (
                        <h6 className="product-list-hero__category product-list-hero__category--pillows">
                            {hero.pre_title}
                        </h6>
                    )}
                    {hero.title_html && (
                        <div
                            className="product-list-hero__title product-list-hero__title--pillows rich-text"
                            dangerouslySetInnerHTML={{
                                __html: hero.title_html,
                            }}
                        />
                    )}
                    {hero.description && (
                        <p className="product-list-hero__description product-list-hero__description--pillows">
                            {hero.description}
                        </p>
                    )}
                </div>
                <div className="product-list-hero__content product-list-hero__content--pillows product-list-hero__content--pillows-mobile">
                    <h6 className="product-list-hero__category product-list-hero__category--pillows">
                        {hero.mobile_pre_title}
                    </h6>
                    {hero.mobile_title_html && (
                        <div
                            className={
                                hero.mobile_title_full_classes
                                    ? "product-list-hero__title product-list-hero__title--pillows rich-text"
                                    : "rich-text"
                            }
                            dangerouslySetInnerHTML={{
                                __html: hero.mobile_title_html,
                            }}
                        />
                    )}
                    <p className="product-list-hero__description product-list-hero__description--pillows">
                        {hero.mobile_description}
                    </p>
                </div>
            </div>
        </section>
    );
};

const GridSection = (props: {
    section: IProps["bed_bases"];
    titleTag: "h1" | "h2";
    subtitleClassName: string;
}): React.JSX.Element => {
    const { section } = props;
    const TitleTag = props.titleTag;
    return (
        <section className="product-list u-margin-bottom--xl">
            <div className="product-list__background l-capped-width">
                <div className="product-list__header product-list__header--bed-bases">
                    <div className="product-list__header-copy product-list__header-copy--bed-bases">
                        <TitleTag className="product-list__title">
                            {section.title}
                        </TitleTag>
                        <div
                            className={props.subtitleClassName}
                            dangerouslySetInnerHTML={{
                                __html: section.subtitle_html,
                            }}
                        />
                    </div>
                    {section.header_image_url && (
                        <img
                            alt={section.header_image_alt}
                            src={section.header_image_url}
                            className="product-list__header-image responsive-img"
                            loading="lazy"
                            decoding="async"
                        />
                    )}
                </div>
                {section.show_grid && (
                    <div className="product-list__content u-flex-container">
                        {section.cards.map((card, i) => (
                            <ProductCard
                                key={i}
                                card={card}
                                cssClass="bases-card"
                                auxCopy={true}
                            />
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
};

export const Template = (props: IProps): React.JSX.Element => {
    const staticURL = useStaticURL();
    const toolboxMap = buildToolboxPageMap(props);
    // Flickity carousel on the bed-bases selection columns; watchCSS gates
    // activation to the mobile breakpoint.
    const selectionRef = React.useRef<HTMLDListElement>(null);
    useFlickity(selectionRef, {
        module: "flickity",
        options: {
            watchCSS: true,
            draggable: true,
            wrapAround: true,
            prevNextButtons: false,
            pageDots: true,
        },
    });
    // The extra_copy callout map: the Toolbox keys (any Toolbox block an
    // editor places there already islanded pre-conversion), the bed-bases
    // bespoke heading/link_banner branches, the primitives as Wagtail
    // render_basic, and the value_prop_bar alias.
    const calloutMap = {
        ...toolboxMap,
        heading: (block: { value: Immutable<CharBlockValue> }) => (
            <div className="bed-bases-callout__title l-capped-width">
                {block.value}
            </div>
        ),
        paragraph: (block: { value: Immutable<RichTextBlockValue> }) => (
            <div dangerouslySetInnerHTML={{ __html: block.value }} />
        ),
        image: (block: { value: Immutable<ImageChooserBlockValue> }) => (
            <img
                src={block.value.url}
                alt={block.value.default_alt_text}
                width={block.value.width}
                height={block.value.height}
                loading="lazy"
                decoding="async"
            />
        ),
        html: (block: { value: Immutable<RawHTMLBlockValue> }) => (
            <RawHTMLBlock value={block.value} />
        ),
        value_prop_bar: (block: {
            value: Immutable<ValuePropBarBlockValue>;
        }) => <ValuePropsBarBlock valuePropsData={block.value} />,
        link_banner: (block: { value: Immutable<ILinkBannerValue> }) => (
            <div className="product-list-link-banner">
                {block.value.image && (
                    <img
                        className="product-list-link-banner__image responsive-img"
                        alt={block.value.image.title}
                        src={
                            block.value.image.renditions["original"]?.url ??
                            block.value.image.url
                        }
                        loading="lazy"
                        decoding="async"
                    />
                )}
                <div className="product-list-link-banner__copy l-capped-width">
                    <h2 className="product-list-link-banner__title">
                        {block.value.text}
                    </h2>
                    {block.value.link && (
                        <a
                            href={block.value.link.url}
                            className="button product-list-link-banner__button"
                        >
                            {props.link_banner_cta_label}
                        </a>
                    )}
                </div>
            </div>
        ),
    };
    return (
        <PageSkeleton pageChrome={props.page_chrome}>
            {props.hero && <Hero hero={props.hero} />}
            <div className="product-lists u-margin-bottom--xl">
                <GridSection
                    section={props.bed_bases}
                    titleTag="h1"
                    subtitleClassName="product-list__description rich-text"
                />
                <GridSection
                    section={props.foundations}
                    titleTag="h2"
                    subtitleClassName="product-list__description"
                />
            </div>
            <div className="bed-bases-selection u-margin-bottom--xl">
                <div className="product-list__background l-capped-width">
                    <div className="product-list__header product-list__header--bed-bases">
                        <h2 className="product-list__title">
                            {props.selection_heading}
                        </h2>
                    </div>
                    <dl
                        className="bed-bases-selection__content"
                        ref={selectionRef}
                    >
                        {props.selection_columns.map((column, i) => (
                            <div key={i} className="bed-bases-selection-column">
                                <img
                                    src={`${staticURL}${column.image_static_path}`}
                                    alt=""
                                    className="bed-bases-selection-column__image responsive-img"
                                    loading="lazy"
                                    decoding="async"
                                />
                                <dt className="bed-bases-selection-column__title">
                                    {column.title}
                                </dt>
                                <dd className="bed-bases-selection-column__copy">
                                    {column.copy}
                                </dd>
                            </div>
                        ))}
                    </dl>
                </div>
            </div>
            <div className="bed-bases-callout">
                {props.page.extra_copy && (
                    <StreamField
                        tagName={React.Fragment}
                        value={props.page.extra_copy.value}
                        components={calloutMap}
                    />
                )}
            </div>
        </PageSkeleton>
    );
};
